import { useEffect, useState } from 'preact/hooks';

import { api, type TrustPage } from '../api/client';
import { Header } from '../components/Header';
import { NoticeItems } from '../components/NoticeItems';
import { pick, translator } from '../i18n';
import { Message } from './Start';

/**
 * The trust page of one survey (SPEC §8.2): how its participants are protected, who sees what,
 * the AI and its provider, deletion, and sub-processors. Everything on it comes from the server,
 * generated from the survey's locked settings.
 */
export function Trust({ surveySlug }: { surveySlug: string }) {
  const [page, setPage] = useState<TrustPage | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    // In the language the participant reads the survey in (the start page and header link pass it).
    const lang = new URLSearchParams(location.search).get('lang') ?? navigator.language.slice(0, 2);
    void api.GET('/api/p/trust/{surveySlug}', { params: { path: { surveySlug }, query: { lang } } }).then(({ data }) => (data ? setPage(data) : setMissing(true)));
  }, [surveySlug]);

  const t = translator(page?.locale ?? navigator.language.slice(0, 2));
  useEffect(() => {
    if (page) {
      document.documentElement.lang = page.locale;
      document.title = `${t('trustTitle')} – ${pick(page.title, page.locale)}`;
    }
  }, [page]);

  if (missing) return <Message text={t('notFound')} />;
  if (!page) return <Message text={t('loading')} />;

  const { notice } = page;
  const contact = notice.administrator?.contact;
  return (
    <div class="page" style={{ '--brand': page.branding.colours.primary, '--on-brand': page.branding.colours.onPrimary ?? '#FFFFFF' }}>
      <Header branding={page.branding} locale={page.locale} t={t} />
      <main class="trust">
        <p class="trust-survey">{pick(page.title, page.locale)}</p>
        <h1>{t('trustTitle')}</h1>
        <p class="privacy-badge">
          <strong>{notice.level}</strong>
        </p>
        <p class="muted">{t('trustIntro')}</p>
        {notice.sections.map((s) => (
          <section key={s.id} aria-labelledby={`trust-${s.id}`}>
            <h2 id={`trust-${s.id}`}>{s.heading}</h2>
            <NoticeItems items={s.items} t={t} />
          </section>
        ))}
        {contact && (
          // Who administers the survey, for participants who would not ask their employer.
          <section aria-labelledby="trust-contact">
            <h2 id="trust-contact">{t('contactHeading')}</h2>
            <p>
              {t('contactText', { name: notice.administrator!.name })}{' '}
              {/^[^\s@]+@[^\s@]+$/.test(contact) ? <a href={`mailto:${contact}`}>{contact}</a> : contact}
            </p>
          </section>
        )}
        <section aria-labelledby="trust-subprocessors">
          <h2 id="trust-subprocessors">{t('subprocessors')}</h2>
          <p class="muted">{t('subprocessorsIntro')}</p>
          <div class="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('subprocessorName')}</th>
                  <th scope="col">{t('subprocessorRole')}</th>
                  <th scope="col">{t('subprocessorLocation')}</th>
                </tr>
              </thead>
              <tbody>
                {notice.subprocessors.map((s, i) => (
                  <tr key={i}>
                    <td>{s.name}</td>
                    <td>{s.role}</td>
                    <td>{s.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
